import * as React from 'react';
import { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import styled, { css, withTheme, keyframes } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import Translate, { translate } from '../Translate';
import Badge from '../Badge';
import Icon from '../Icon';
import { action, computed } from 'mobx';
import { DataStore, TranslationStore, AccessibilityStore } from '../../stores';
import Checkbox from '../Checkbox';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { deepMergeObjectsWithOptions } from '@kurtosys/ksys-app-template/dist/utils/object';
import InlineDisclaimers from '../InlineDisclaimers';

const bounce = keyframes`
    from,
    20%,
    53%,
    80%,
    to {
      animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
      transform: translate3d(0, 0, 0);
    }
  
    40%,
    43% {
      animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
      transform: translate3d(0, -0.5em, 0);
    }
  
    70% {
      animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
      transform: translate3d(0, -0.25em, 0);
    }
  
    90% {
      transform: translate3d(0, -0.05em, 0);
    }
`;

const bounceRule = css`
    ${bounce}
`;

const DownloadControlsWrapper = styled<any, any>('div')`
    ${propStyles};
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

DownloadControlsWrapper.defaultProps = {
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: ['none', 'none none solid none'],
    hover: {
        bgColor: ['transparent', 'gray100'],
    },
};

const Wrapper = styled<any, any>('div')`
    ${propStyles};
`;

Wrapper.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: '1 0 auto',
};

const IconWrapper = styled<any, any>('div')`
    ${propStyles};
`;

IconWrapper.defaultProps = {
    alignItems: 'center',
};

const DownloadButtonIcon = styled<any, any>(Icon)``;

DownloadButtonIcon.defaultProps = {
    marginX: 'sm',
    height: '1.6em',
    width: '1.6em',
};

const PreviewButtonIcon = styled<any, any>(Icon)``;

PreviewButtonIcon.defaultProps = {
    marginX: 'sm',
    height: '1.6em',
    width: '1.6em',
};

const DownloadButton = styled<any, any>('button')`
    ${propStyles};
    &:hover {
        ${DownloadButtonIcon} {
            animation-name: ${bounceRule};
            animation-duration: 1s;
            animation-fill-mode: both;
            transform-origin: center bottom;
        }
    }
`;

DownloadButton.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    paddingX: '0',
    paddingY: '0',
    fontSize: 'inherit',
    fontFamily: 'body',
    fontWeight: 'regular',
    textAlign: 'left',
    color: 'inherit',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
    outline: 'none',
    hover: {
        color: 'primary',
    },
    justifyContent: 'space-between',
    flex: '1 1 auto',
    textDecoration: 'none',
};

const DownloadLink = styled<any, any>('a')`
    ${propStyles};
    &:hover {
        ${DownloadButtonIcon} {
            animation-name: ${bounceRule};
            animation-duration: 1s;
            animation-fill-mode: both;
            transform-origin: center bottom;
        }
    }
`;

// use same styles as the button
DownloadLink.defaultProps = DownloadButton.defaultProps;

const PreviewButton = styled<any, any>('button')`
    ${propStyles};
    &:hover {
        ${PreviewButtonIcon} {
            animation-name: ${bounceRule};
            animation-duration: 1s;
            animation-fill-mode: both;
            transform-origin: center bottom;
        }
    }
`;

PreviewButton.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    paddingX: '0',
    paddingY: '0',
    fontSize: 'inherit',
    fontFamily: 'body',
    fontWeight: 'regular',
    textAlign: 'left',
    color: 'inherit',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
    outline: 'none',
    hover: {
        color: 'primary',
    },
    justifyContent: 'flex-end',
    flex: '0 0 auto',
};

const checkboxStyleProps = {
    CheckboxChecked: {
        height: '1.4em',
        width: '1.4em',
        marginX: 'xs',
    },
    CheckboxUnchecked: {
        height: '1.4em',
        width: '1.4em',
        marginX: 'xs',
    },
};

interface DownloadControlsTheme {
    DocumentTitle?: any;
    DocumentTitleControls?: any;
    Badge?: any;
    Icon?: any;
}

interface DownloadControlsProps {
    theme?: any;
    dataStore?: DataStore;
    translationStore?: TranslationStore;
    label?: string;
    groupLabel?: string;
    showCount?: boolean;
    showDownload?: boolean;
    showPreview?: boolean;
    count?: number;
    disabled?: boolean;
    downloadButtonFileName?: string;
    previewButtonFileName?: string;
    isMobile?: boolean;
    showLabel?: boolean;
    showGroupLabel?: boolean;
    fileName?: string;
    showCheckbox?: boolean;
    document?: any;
    selected?: boolean;
    metricContext?: string;
    href?: string;
    documentType?: string;
    entityWrapper?: any;
    accessibilityStore?: AccessibilityStore;
    onClick?(): void;
    onPreviewClick?(): void;
    onChange?(document: any): void;
}

@inject('accessibilityStore', 'dataStore', 'translationStore')
@observer
class DownloadControls extends Component<DownloadControlsProps> {
    constructor(props: DownloadControlsProps) {
        super(props);
        this.handleOnChange = this.handleOnChange.bind(this);
    }

    @computed
    get theme() {
        const { theme } = this.props;
        const componentTheme: DownloadControlsTheme = {};
        return { ...theme, ...componentTheme };
    }

    @action
    handleOnChange(document) {
        const { onChange } = this.props;
        if (onChange) {
            onChange(document);
        }
    }

    @computed
    get title() {
        const {
            showLabel,
            label,
            showGroupLabel,
            groupLabel,
            translationStore,
        } = this.props;
        let text = '';
        if (showGroupLabel && groupLabel) {
            text += ` ${groupLabel}`;
        }
        if (showLabel && label) {
            text = ` ${label}`;
        }
        return translate(translationStore, text);
    }

    @computed
    get downloadTitle() {
        const { translationStore } = this.props;
        const text = `${this.title} Download`;
        return translate(translationStore, text.trim());
    }

    @computed
    get previewTitle() {
        const { translationStore } = this.props;
        const text = `${this.title} Preview`;
        return translate(translationStore, text.trim());
    }

    @computed
    get showLabel() {
        const { showLabel = true } = this.props;
        return showLabel;
    }

    @computed
    get showGroupLabel() {
        const { showGroupLabel = false } = this.props;
        return showGroupLabel;
    }

    // Set up the object assign to avoid breaking existing style selectors
    @computed
    get downloadControlsTheme() {
        const { isMobile } = this.props;
        const { theme } = this;
        return isMobile
            ? { ...theme.DownloadControls, ...theme.DownloadControlsMobile }
            : theme.DownloadControls;
    }

    @computed
    get downloadButtonTheme() {
        const { isMobile } = this.props;
        const { theme } = this;
        return isMobile
            ? { ...theme.DownloadButton, ...theme.DownloadButtonMobile }
            : theme.DownloadButton;
    }

    @computed
    get downloadButtonIconWrapperTheme() {
        const { isMobile } = this.props;
        const { theme } = this;
        return isMobile
            ? {
                  ...theme.DownloadButtonIconWrapper,
                  ...theme.DownloadButtonIconWrapperMobile,
              }
            : theme.DownloadButtonIconWrapper;
    }

    // default icon to center when no label and right for mobile
    @computed
    get downloadButtonIconCenterTheme() {
        const { theme } = this;
        let buttonIconTheme = theme.DownloadButtonIcon;
        if (!this.showLabel) {
            buttonIconTheme = {
                ...theme.DownloadButtonIcon,
                ...theme.DownloadButtonIconNoLabel,
            };
        }
        return buttonIconTheme;
    }

    @computed
    get downloadButtonIconTheme() {
        const { isMobile } = this.props;
        const { theme } = this;
        return isMobile
            ? {
                  ...this.downloadButtonIconCenterTheme,
                  ...theme.DownloadButtonIconMobile,
              }
            : this.downloadButtonIconCenterTheme;
    }

    // default icon to center when no label and right for mobile
    @computed
    get previewButtonIconCenterTheme() {
        const { theme } = this;
        let buttonIconTheme = theme.PreviewButtonIcon;
        if (!this.showLabel) {
            buttonIconTheme = {
                ...theme.PreviewButtonIcon,
                ...theme.PreviewButtonIconNoLabel,
            };
        }
        return buttonIconTheme;
    }

    @computed
    get previewButtonIconTheme() {
        const { isMobile } = this.props;
        const { theme } = this;
        return isMobile
            ? {
                  ...this.previewButtonIconCenterTheme,
                  ...theme.PreviewButtonIconMobile,
              }
            : this.previewButtonIconCenterTheme;
    }

    @computed
    get downloadCountTheme() {
        const { isMobile } = this.props;
        const { theme } = this;
        return isMobile
            ? { ...theme.DownloadCount, ...theme.DownloadCountMobile }
            : theme.DownloadCount;
    }

    @computed
    get metricId() {
        const { metricContext } = this.props;
        return `downloadControl-${metricContext}`;
    }

    @computed
    get useDownloadLink(): boolean {
        const { href } = this.props;
        return !isNullOrEmpty(href);
    }

    // Use download link when 1 document so that the GET http method can be used. ZIP currently doesn't support multiple document codes
    @computed
    get DownloadComponent(): React.ComponentType {
        if (this.useDownloadLink) {
            return DownloadLink;
        }
        return DownloadButton;
    }

    @computed
    get downloadComponentProps(): any {
        const { fileName, disabled, onClick, href } = this.props;
        const componentProps: any = {
            disabled,
            title: this.downloadTitle,
            'aria-label': this.downloadTitle,
            // Used for Adobe analytics tracking
            'data-metrics-link-type': 'download',
            'data-metrics-link-dest': fileName,
            'data-metric-id': this.metricId,
            ...this.downloadButtonTheme,
        };
        if (this.useDownloadLink) {
            componentProps.href = href;
        } else {
            componentProps.onClick = onClick;
        }
        return componentProps;
    }

    render() {
        const {
            dataStore,
            translationStore,
            label,
            groupLabel,
            showCount = true,
            showDownload = true,
            showPreview = false,
            count = 0,
            disabled,
            downloadButtonFileName = 'DownloadIcon',
            previewButtonFileName = 'PreviewIcon',
            fileName,
            showCheckbox = false,
            selected,
            documentType,
            entityWrapper,
            onClick,
            onPreviewClick,
            onChange,
            document,
            metricContext,
            accessibilityStore,
            ...props
        } = this.props;
        const {
            downloadControlsTheme,
            downloadButtonTheme,
            downloadButtonIconWrapperTheme,
            downloadButtonIconTheme,
            downloadCountTheme,
            showLabel,
            showGroupLabel,
            theme,
            DownloadComponent,
        } = this;

        const documentCulture = document && document.cultureCode;
        let conditionalDisclaimers;
        if (dataStore && dataStore.disclaimerStore) {
            conditionalDisclaimers = dataStore.disclaimerStore.getConditionalDisclaimersForDocument(
                documentType,
                documentCulture
            );
        }

        const superscriptThemeDefault = deepMergeObjectsWithOptions(
            { arrayMergeStrategy: 'DeepMerge' },
            {
                Superscript: {
                    padding: '0 0 10px 0',
                },
            },
            theme
        );

        return (
            <Fragment>
                {showCheckbox && (
                    <Checkbox
                        checked={selected}
                        onChange={onChange}
                        theme={{
                            ...theme,
                            ...checkboxStyleProps,
                            ...{
                                checkBoxLabel: {
                                    opacity: disabled ? 0.5 : 1,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    color: 'body',
                                },
                            },
                        }}
                        metricContext={this.metricId}
                    />
                )}

                <Wrapper {...props} {...downloadControlsTheme}>
                    {showDownload && (
                        <DownloadComponent {...this.downloadComponentProps}>
                            <InlineDisclaimers
                                conditionalDisclaimers={conditionalDisclaimers}
                                entityWrapper={entityWrapper}
                                theme={superscriptThemeDefault}
                            >
                                {groupLabel && showGroupLabel && (
                                    <Translate>{groupLabel}</Translate>
                                )}
                                {label && showLabel && (
                                    <Translate>{label}</Translate>
                                )}
                            </InlineDisclaimers>
                            <IconWrapper {...downloadButtonIconWrapperTheme}>
                                <DownloadButtonIcon
                                    aria-label={accessibilityStore.buttonAccessibilityValue(
                                        'DownloadButtonIcon'
                                    )}
                                    fileName={downloadButtonFileName}
                                    {...downloadButtonIconTheme}
                                />
                            </IconWrapper>
                        </DownloadComponent>
                    )}
                    {showPreview && (
                        <PreviewButton
                            title={this.previewTitle}
                            aria-label={this.previewTitle}
                            onClick={onClick}
                            disabled={disabled}
                            // Used for Adobe analytics tracking
                            data-metrics-link-type={'preview'}
                            data-metrics-link-dest={fileName}
                            data-metric-id={`downloadControl-preview-${metricContext}`}
                            {...downloadButtonTheme}
                        >
                            <IconWrapper {...downloadButtonIconWrapperTheme}>
                                <PreviewButtonIcon
                                    aria-label={accessibilityStore.buttonAccessibilityValue(
                                        'PreviewButtonIcon'
                                    )}
                                    fileName={previewButtonFileName}
                                    onClick={onPreviewClick}
                                />
                            </IconWrapper>
                        </PreviewButton>
                    )}
                    {showCount && (
                        <Badge bgColor="primary" {...downloadCountTheme}>
                            {count}
                        </Badge>
                    )}
                </Wrapper>
            </Fragment>
        );
    }
}

export default withTheme(DownloadControls) as any;



// WEBPACK FOOTER //
// ./src/Components/Document/DownloadControls.tsx